import {useThemeColors} from '@nfq/colors';
import {useFormField, type ValidationFunc} from '@nfq/react-form';

import type {DateObject} from 'react-multi-date-picker';

/**
 * A custom React hook that provides form field management and validation for range date picker components.
 * This hook integrates with the form library to handle state management, validation, and theme colors for date range selection.
 * It returns the necessary props and handlers to connect a date range picker component to the form system.
 *
 * @param name          The unique identifier for the form field used to register and track the field state.
 * @param initialValue  The initial date range value as an array containing start and end dates.
 * @param keepOnUnmount Whether to preserve the field value when the component unmounts from the DOM.
 * @param onChange      Callback function that is invoked when the date range value changes.
 * @param validation    The validation function to apply to the date range field for form validation.
 * @returns An object containing colors, error state, change handler, and current value for the date range picker.
 *
 * @example
 * ```tsx
 * const { colors, error, handleChange, value } = useRangeDatePicker(
 *   'dateRange',
 *   [new Date(), new Date()],
 *   false,
 *   (dates) => console.log(dates),
 *   (value) => value.length === 2 ? null : 'Please select both dates'
 * );
 * ```
 */
export const useRangeDatePicker = (
    name: string,
    initialValue: Date[],
    keepOnUnmount: boolean,
    onChange: (value: [Date, Date]) => void,
    validation: ValidationFunc
) => {
    const colors = useThemeColors();
    const {error, handleFieldChange, value} = useFormField(name, initialValue, initialValue, validation, keepOnUnmount);
    /**
     * Handles the change event when a new date range is selected.
     * It checks if the user has selected both `from` and `to` dates, converts them to `Date` objects, and triggers the `onChange` callback with the updated values.
     *
     * @param newValue An array of `DateObject[]` representing the selected date range. It should contain two dates: the start (`from`) and end (`to`) of the range.
     */
    const handleChange = (newValue: DateObject[]) => {
        if (newValue.length > 1) {
            const from = newValue[0].toDate();
            const to = newValue[1].toDate();

            handleFieldChange([from, to]);
            onChange([from, to]);
        }
    };

    return {
        colors,
        error,
        handleChange,
        value
    };
};